import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const ReReprint = () => {
  const [searchParams] = useSearchParams();
  const [labInfo, setLabInfo] = useState(null);
  const accountIds = useMemo(() => {
    const ids = searchParams.get("accountIds");
    return ids ? ids : "all";
  }, [searchParams]);
  const date = searchParams.get("date") || new Date().toISOString().split("T")[0];
  const dontPrintEbilling = searchParams.get("dontPrintEbilling") === "true";
  const dontPrintZeroBalance = searchParams.get("dontPrintZeroBalance") === "true";
  const reprint = searchParams.get("reprint") === "true";
  const [statements, setStatements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printReady, setPrintReady] = useState(false);
  const [previousRunDate, setPreviousRunDate] = useState(null);
  const [runDate, setRunDate] = useState(null);
  useEffect(() => {
    const fetchLabInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://hms.backend.hiragroupcloud.com/api/auth/users",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success && response.data.users.length > 0) {
          setLabInfo(response.data.users[0]);
        }
      } catch (err) {
        console.error("Lab info fetch error:", err);
      }
    };
    fetchLabInfo();
  }, []);
  useEffect(() => {
    const fetchStoredStatements = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://hms.backend.hiragroupcloud.com/api/statements/stored-statement",
          {
            params: {
              accountIds,
              date,
              dontPrintEbilling: dontPrintEbilling.toString(),
              dontPrintZeroBalance: dontPrintZeroBalance.toString()
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success && response.data.data && response.data.data.statements) {
          const statementsData = response.data.data.statements;

          // Process statements to include grouped payments/credits
          const processedStatements = statementsData.map(statement => {
            // Get all invoices
            const allInvoices = statement.invoices || [];

            // Create invoice lookup
            const invoiceLookup = {};
            allInvoices.forEach(inv => {
              if (inv.invoiceNumber) {
                invoiceLookup[inv.invoiceNumber] = inv;
              }
            });
            // Calculate aging totals
            const agingTotals = {
              oneTo30Days: statement.oneTo30Days || 0,
              thirtyOneTo60Days: statement.thirtyOneTo60Days || 0,
              sixtyOneTo90Days: statement.sixtyOneTo90Days || 0,
              ninetyPlusDays: statement.ninetyPlusDays || 0,
              totalBalance: statement.newBalance || 0
            };
            // Group invoices by provider - ONLY include non-previous balance invoices
            const groupedByProvider = {};
            const displayedInvoices = new Set();
            // Process only NON-previous balance invoices
            allInvoices.forEach(invoice => {
              // Skip previous balance invoices entirely - they will be shown as a single row
              if (invoice.isPreviousBalance) {
                return;
              }
              const invoiceKey = invoice.invoiceNumber;
              if (displayedInvoices.has(invoiceKey)) {
                return;
              }
              displayedInvoices.add(invoiceKey);
              const providerName = invoice.providerName || 'Unknown Provider';

              if (!groupedByProvider[providerName]) {
                groupedByProvider[providerName] = {
                  providerId: invoice.providerId,
                  providerName: providerName,
                  invoices: [],
                  total: 0,
                  previousBalanceAmount: 0,
                  totalDebits: 0,
                  totalCredits: 0,
                  providerSubtotal: 0
                };
              }
              // Calculate outstanding amount
              const totalPayments = (invoice.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
              const totalCredits = (invoice.credits || []).reduce((sum, c) => sum + (c.amount || 0), 0);
              const outstanding = (invoice.originalAmount || invoice.total || 0) - totalPayments - totalCredits;
              // Track debits for provider
              groupedByProvider[providerName].totalDebits += (invoice.originalAmount || invoice.total || 0);
              // Track credits from payments
              (invoice.payments || []).forEach(p => {
                groupedByProvider[providerName].totalCredits += p.amount || 0;
              });

              (invoice.credits || []).forEach(c => {
                groupedByProvider[providerName].totalCredits += c.amount || 0;
              });
              groupedByProvider[providerName].invoices.push({
                ...invoice,
                outstanding: outstanding,
                transactions: invoice.transactions || []
              });
              groupedByProvider[providerName].total += outstanding;
            });
            // Add previous balance amount from providers array
            (statement.providers || []).forEach(prov => {
              const previousBalance = Number(prov.previousBalance || 0);
              if (previousBalance > 0) {
                const providerName = prov.providerName;
                if (!groupedByProvider[providerName]) {
                  groupedByProvider[providerName] = {
                    providerId: prov.providerId,
                    providerName: providerName,
                    invoices: [],
                    total: 0,
                    previousBalanceAmount: previousBalance,
                    totalDebits: 0,
                    totalCredits: 0,
                    providerSubtotal: 0
                  };
                } else {
                  groupedByProvider[providerName].previousBalanceAmount += previousBalance;
                }
              }
            });
            // Calculate provider subtotals (initial, before adding previous/standalone credits)
            Object.keys(groupedByProvider).forEach(providerName => {
              const provider = groupedByProvider[providerName];
              provider.providerSubtotal = (provider.previousBalanceAmount || 0) +
                                         (provider.totalDebits || 0) -
                                         (provider.totalCredits || 0);
            });
            // Group previous transactions by provider (payments/credits against previous balance)
            const previousTransactionsByProvider = {};

            // Process groupedPrevious - these are payments/credits against previous balance
            (statement.groupedPrevious || []).forEach(group => {
              // Determine provider for this group
              let providerName = 'Unknown Provider';

              // Try to get provider from items
              if (group.items && group.items.length > 0) {
                providerName = group.items[0].providerName || 'Unknown Provider';
              }
              // Try from invoice numbers
              else if (group.invoiceNumbers && group.invoiceNumbers.length > 0) {
                const inv = allInvoices.find(inv => inv.invoiceNumber === group.invoiceNumbers[0]);
                if (inv) {
                  providerName = inv.providerName || 'Unknown Provider';
                }
              }
              if (!previousTransactionsByProvider[providerName]) {
                previousTransactionsByProvider[providerName] = [];
              }
              previousTransactionsByProvider[providerName].push(group);
            });
            // Group standalone transactions by provider (these are payments/credits that couldn't be attached)
            const standaloneByProvider = {};

            (statement.sortedStandalone || []).forEach(group => {
              // Determine provider for this group
              let providerName = 'Unknown Provider';

              // Try to get provider from items
              if (group.items && group.items.length > 0) {
                providerName = group.items[0].providerName || 'Unknown Provider';
              }
              // Try from invoice numbers
              else if (group.invoiceNumbers && group.invoiceNumbers.length > 0) {
                const inv = allInvoices.find(inv => inv.invoiceNumber === group.invoiceNumbers[0]);
                if (inv) {
                  providerName = inv.providerName || 'Unknown Provider';
                }
              }
              if (!standaloneByProvider[providerName]) {
                standaloneByProvider[providerName] = [];
              }
              standaloneByProvider[providerName].push(group);
            });

            // NEW FIX: Add previous and standalone credits to totalCredits for each provider
            // Handle previous transactions
            Object.keys(previousTransactionsByProvider).forEach(providerName => {
              if (!groupedByProvider[providerName]) {
                // Create entry if provider doesn't exist (e.g., only credits)
                groupedByProvider[providerName] = {
                  providerId: '', // Can be improved by fetching from group.items if needed
                  providerName: providerName,
                  invoices: [],
                  total: 0,
                  previousBalanceAmount: 0,
                  totalDebits: 0,
                  totalCredits: 0,
                  providerSubtotal: 0
                };
              }
              const prevCredits = previousTransactionsByProvider[providerName].reduce((sum, group) => sum + (group.amount || 0), 0);
              groupedByProvider[providerName].totalCredits += prevCredits;
            });

            // Handle standalone transactions
            Object.keys(standaloneByProvider).forEach(providerName => {
              if (!groupedByProvider[providerName]) {
                // Create entry if provider doesn't exist
                groupedByProvider[providerName] = {
                  providerId: '', // Can be improved by fetching from group.items if needed
                  providerName: providerName,
                  invoices: [],
                  total: 0,
                  previousBalanceAmount: 0,
                  totalDebits: 0,
                  totalCredits: 0,
                  providerSubtotal: 0
                };
              }
              const standaloneCredits = standaloneByProvider[providerName].reduce((sum, group) => sum + (group.amount || 0), 0);
              groupedByProvider[providerName].totalCredits += standaloneCredits;
            });

            // NEW FIX: Recalculate provider subtotals after adding all credits
            Object.keys(groupedByProvider).forEach(providerName => {
              const provider = groupedByProvider[providerName];
              provider.providerSubtotal = (provider.previousBalanceAmount || 0) + (provider.totalDebits || 0) - (provider.totalCredits || 0);
            });

            // NEW FIX: Recalculate total new balance as sum of all provider subtotals for consistency
            const calculatedTotalBalance = Object.values(groupedByProvider).reduce((sum, prov) => sum + (prov.providerSubtotal || 0), 0);

            return {
              ...statement,
              groupedByProvider,
              groupedPrevious: statement.groupedPrevious || [],
              sortedStandalone: statement.sortedStandalone || [],
              agingTotals: {
                ...agingTotals,
                totalBalance: calculatedTotalBalance // Override backend totalBalance with calculated sum
              },
              displayDate: response.data.data.runDate || date,
              previousTransactionsByProvider,
              standaloneByProvider
            };
          });
          setStatements(processedStatements);
          setPreviousRunDate(response.data.data.previousRunDate || null);
          setRunDate(response.data.data.runDate || date);
          setPrintReady(true);
        } else {
          const errorMsg = response.data.message || "No stored statements found";
          setError(errorMsg);
          setStatements([]);
        }
      } catch (error) {
        console.error("Fetch stored statements error:", error);
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.error ||
                            error.message ||
                            "An error occurred while fetching stored statements.";
        setError(errorMessage);
        setStatements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoredStatements();
  }, [accountIds, date, dontPrintEbilling, dontPrintZeroBalance]);
  const handlePrint = () => {
    window.print();
  };
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "Invalid Date";

    let dateObj;
    if (typeof dateStr === 'string') {
      if (dateStr.includes('T')) {
        dateObj = new Date(dateStr);
      } else {
        dateObj = new Date(dateStr + 'T00:00:00');
      }
    } else if (dateStr instanceof Date) {
      dateObj = dateStr;
    } else {
      return "Invalid Date";
    }

    if (isNaN(dateObj.getTime())) return "Invalid Date";

    const day = dateObj.getDate();
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    return `${day} ${month} ${year}`;
  };
  const formatCurrency = (amount, showZero = false) => {
    if (typeof amount !== "number" || isNaN(amount)) return "";
    if (amount === 0 && !showZero) return "";
    const absAmount = Math.abs(amount);
    const sign = amount < 0 ? "-" : "";
    return `${sign}£${absAmount.toFixed(2)}`;
  };
  const formatCredit = (amount) => {
    const safeAmount = amount || 0;
    return safeAmount !== 0 ? `-${formatCurrency(Math.abs(safeAmount))}` : '';
  };
  const removeLeadingZeros = (str) => {
    if (!str) return '';
    const result = String(str).replace(/^0+/, '');
    return result === '' ? '0' : result;
  };
  const getPatientName = (invoice) => {
    if (invoice.patientName) return invoice.patientName;
    if (invoice.patientFirstName || invoice.patientLastName) {
      return `${invoice.patientFirstName || ''} ${invoice.patientLastName || ''}`.trim();
    }
    return '';
  };
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading stored statements for {formatDisplayDate(date)}...</p>
        <p className="text-muted small">Account: {accountIds === "all" ? "All Accounts" : accountIds}</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="alert alert-danger m-4" role="alert">
        <h4 className="alert-heading">Error Loading Statements</h4>
        <p>{error}</p>
        <hr />
        <div className="mb-3">
          <p><strong>Search Criteria:</strong></p>
          <ul className="mb-0">
            <li>Date: {date} ({formatDisplayDate(date)})</li>
            <li>Account: {accountIds === "all" ? "All Accounts" : accountIds}</li>
            <li>Don't Print Ebilling: {dontPrintEbilling.toString()}</li>
            <li>Don't Print Zero Balance: {dontPrintZeroBalance.toString()}</li>
          </ul>
        </div>
        <button
          className="btn btn-outline-danger"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
        <button
          className="btn btn-outline-secondary ms-2"
          onClick={() => window.close()}
        >
          Close Window
        </button>
      </div>
    );
  }
  if (statements.length === 0) {
    return (
      <div className="alert alert-warning m-4" role="alert">
        <h4 className="alert-heading">No Statements Found</h4>
        <p>No stored statements found for the selected criteria.</p>
        <div className="mt-3">
          <p><strong>Search Criteria:</strong></p>
          <ul className="mb-0">
            <li>Date: {date} ({formatDisplayDate(date)})</li>
            <li>Account: {accountIds === "all" ? "All Accounts" : accountIds}</li>
            <li>Don't Print Ebilling: {dontPrintEbilling.toString()}</li>
            <li>Don't Print Zero Balance: {dontPrintZeroBalance.toString()}</li>
          </ul>
        </div>
        <button
          className="btn btn-outline-warning mt-3"
          onClick={() => window.close()}
        >
          Close Window
        </button>
      </div>
    );
  }
  return (
    <div className="statement-container">
      <div className="header-controls">
        <div className="button-container">
          <button
            className="print-button"
            onClick={handlePrint}
            disabled={!printReady}
          >
            Print
          </button>
        </div>
      </div>
      {statements.map((statement, index) => {
        // Track displayed invoices to prevent duplicates
        const displayedInvoices = new Set();

        // Group providers properly
        const providerGroups = {};

        Object.entries(statement.groupedByProvider || {}).forEach(([providerName, group]) => {
          if (!providerGroups[providerName]) {
            providerGroups[providerName] = {
              previousBalanceAmount: group.previousBalanceAmount || 0,
              currentGroups: [],
              previousBalanceTransactions: [],
              totalDebits: group.totalDebits || 0,
              totalCredits: group.totalCredits || 0,
              providerSubtotal: group.providerSubtotal || 0
            };
          }
          // Filter unique invoices in this group (these are already non-previous balance invoices)
          const uniqueInvoices = group.invoices.filter(inv => {
            const invoiceKey = inv.invoiceNumber;
            if (displayedInvoices.has(invoiceKey)) {
              return false;
            }
            displayedInvoices.add(invoiceKey);
            return true;
          });

          group.invoices = uniqueInvoices;
          providerGroups[providerName].currentGroups.push(group);
        });
        const previousTransactionsByProvider = statement.previousTransactionsByProvider || {};
        const standaloneByProvider = statement.standaloneByProvider || {};
        return (
          <div
            key={`${statement.accountId}-${index}`}
            className="statement-section"
            style={{ pageBreakBefore: index > 0 ? "always" : "auto" }}
          >
            <div className="header-section">
              <div className="lab-info">
                <strong>{labInfo?.labName || "Lab Name"}</strong>
                <br />
                {labInfo?.address || "Address not available"}
                <br />
                {labInfo?.city}, {labInfo?.state} {labInfo?.zipCode}
              </div>
              <div className="header-right">
                <div className="statement-right">
                  <strong>Statement for {formatDisplayDate(statement.displayDate || runDate)}</strong>
                  <br />
                  {labInfo?.telephone || "Not available"}
                  <br />
                  {labInfo?.labEmail || "Email not available"}
                  {previousRunDate && (
                    <div className="previous-statement">
                      Previous: {formatDisplayDate(previousRunDate)}
                    </div>
                  )}
                </div>
                <div className="logo-container-right">
                  <img src="./log.png" alt="Lab Logo" className="header-logo" />
                </div>
              </div>
            </div>
            <h3 className="statement-title">
              Statement for {formatDisplayDate(statement.displayDate || runDate)}
            </h3>
            <div className="client-payment-wrapper">
              <div className="client-info">
                <strong>{statement.accountHolder}</strong>
                <br />
                {statement.address || "Address not available"}
                <br />
                {[
                  statement.city,
                  statement.state,
                  statement.zip
                ].filter(Boolean).join(", ")}
              </div>
              <div className="payment-details">
                {statement.note ? (
                  statement.note.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))
                ) : (
                  <>
                    Telephone: {statement.telephone || 'Not available'}<br />
                    Email: {statement.email || 'Not available'}
                  </>
                )}
              </div>
            </div>
            <div className="table-wrapper">
              <table className="statement-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(providerGroups)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([providerName, groups]) => {

                    const providerPreviousBalance = groups.previousBalanceAmount;
                    const providerPreviousTransactions = previousTransactionsByProvider[providerName] || [];

                    // Use providerSubtotal from calculation
                    const providerSubtotal = groups.providerSubtotal;
                    return (
                      <React.Fragment key={providerName}>
                        <tr className="provider-row">
                          <td colSpan="4">
                            <strong>{providerName}</strong>
                          </td>
                        </tr>

                        {/* Previous Balance Row - Only show if there is a previous balance amount */}
                        {providerPreviousBalance > 0 && (
                          <tr>
                            <td>{formatDisplayDate(previousRunDate || statement.previousRunDate || '')}</td>
                            <td><strong>Previous Balance</strong></td>
                            <td>{formatCurrency(providerPreviousBalance)}</td>
                            <td></td>
                          </tr>
                        )}

                        {/* Grouped Previous Transactions - Payments/Credits against previous balance */}
                        {providerPreviousTransactions.map((group, idx) => (
                          <tr key={`prev-group-${providerName}-${idx}`} className="sub-row">
                            <td>{formatDisplayDate(group.date)}</td>
                            <td style={{ paddingLeft: '20px' }}>{group.displayDescription}</td>
                            <td></td>
                            <td>{formatCredit(group.amount)}</td>
                          </tr>
                        ))}

                        {/* Current invoices - Only non-previous balance invoices */}
                        {groups.currentGroups.map((group, groupIndex) => (
                          <React.Fragment key={`curr-group-${providerName}-${groupIndex}`}>
                            {group.invoices.map((invoice, invIndex) => {
                              const patientName = getPatientName(invoice);
                              const invoiceDate = invoice.deliveryDate || invoice.invoiceDate;
                              const formattedInvoiceNumber = removeLeadingZeros(invoice.invoiceNumber);
                              const originalAmount = invoice.originalAmount || invoice.total || invoice.amount || 0;
                              return (
                                <React.Fragment key={`inv-${invoice.invoiceNumber}`}>
                                  <tr>
                                    <td>{formatDisplayDate(invoiceDate)}</td>
                                    <td>Case Invoice {formattedInvoiceNumber} / {patientName}</td>
                                    <td>{formatCurrency(originalAmount)}</td>
                                    <td></td>
                                  </tr>

                                  {/* Transactions for this invoice */}
                                  {invoice.transactions
                                    ?.sort((a, b) => new Date(a.date) - new Date(b.date))
                                    .map((trans, tIdx) => (
                                      <tr key={`trans-${invoice.invoiceNumber}-${tIdx}`} className="sub-row">
                                        <td>{formatDisplayDate(trans.date)}</td>
                                        <td style={{ paddingLeft: '20px' }}>{trans.displayDescription}</td>
                                        <td></td>
                                        <td>{formatCredit(trans.amount)}</td>
                                      </tr>
                                    ))}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        ))}

                        {/* Standalone transactions per provider */}
                        {(standaloneByProvider[providerName] || []).map((group, idx) => (
                          <tr key={`standalone-${providerName}-${idx}`} className="sub-row">
                            <td>{formatDisplayDate(group.date)}</td>
                            <td style={{ paddingLeft: '20px' }}>{group.displayDescription}</td>
                            <td></td>
                            <td>{formatCredit(group.amount)}</td>
                          </tr>
                        ))}

                        {/* Subtotal per provider */}
                        <tr className="subtotal-row">
                          <td colSpan="2"><strong>Subtotal</strong></td>
                          <td>{formatCurrency(providerSubtotal)}</td>
                          <td></td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Aging Summary Table */}
            <div className="aging-summary">
              <table className="aging-table">
                <thead>
                  <tr>
                    <th>1-30 Days</th>
                    <th>31-60 Days</th>
                    <th>61-90 Days</th>
                    <th>91+ Days</th>
                    <th>New Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formatCurrency(statement.agingTotals?.oneTo30Days || 0, true)}</td>
                    <td>{formatCurrency(statement.agingTotals?.thirtyOneTo60Days || 0, true)}</td>
                    <td>{formatCurrency(statement.agingTotals?.sixtyOneTo90Days || 0, true)}</td>
                    <td>{formatCurrency(statement.agingTotals?.ninetyPlusDays || 0, true)}</td>
                    <td>{formatCurrency(statement.agingTotals?.totalBalance || 0, true)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="statement-footer">
              <div>{statement.accountHolder}</div>
              <div>{statement.footer || 'No footer available'}</div>
              <div>Page {index + 1} of {statements.length}</div>
            </div>
          </div>
        );
      })}
      <style>{`
        .statement-container {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 14px;
          color: #000;
          padding: 20px;
          max-width: 100%;
          margin: auto;
        }
  
        .header-controls {
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 15px;
        }
  
        .logo-container-right {
          text-align: right;
          min-width: 150px;
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
        }
  
        .header-logo {
          height: 60px;
          max-width: 120px;
          object-fit: contain;
        }
        .button-container {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }
  
        .print-button {
          background-color: #004aad;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 16px;
        }
  
        .print-button:hover {
          background-color: #003580;
        }
  
        .print-button:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
  
        .statement-section {
          margin-bottom: 50px;
        }
  
        .header-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
          padding-bottom: 15px;
          border-bottom: 1px solid #ccc;
        }
  
        .lab-info {
          flex: 1;
        }
  
        .header-right {
          display: flex;
          align-items: flex-start;
          gap: 20px;
        }
  
        .statement-right {
          text-align: left;
        }
     
        .previous-statement {
          font-size: 12px;
          color: #666;
          margin-top: 5px;
        }
     
        .statement-title {
          text-align: right;
          margin-top: 0;
          margin-bottom: 30px;
        }
  
        .client-payment-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          margin-bottom: 20px;
          gap: 20px;
        }
        .client-info {
          font-size: 13px;
        }
        .payment-details {
          max-width: 100%;
          font-size: 13px;
          text-align: left;
        }
        .table-wrapper {
          overflow-x: auto;
          margin-bottom: 30px;
        }
  
        .statement-table {
          width: 100%;
          border-collapse: collapse;
        }
     
        .statement-table th,
        .statement-table td {
          border: 1px solid #000;
          padding: 4px;
        }
     
        .statement-table th {
          background-color: #004aad;
          color: white;
          font-weight: bold;
          font-size: 13px;
          text-align: left;
        }
     
        .statement-table.center th,
        .statement-table.center td {
          text-align: center;
        }
     
        .statement-table td:nth-child(2) {
          text-align: left;
        }
     
        .statement-table td:nth-child(3),
        .statement-table td:nth-child(4) {
          text-align: right;
        }
     
        .provider-row {
          font-weight: bold;
        }
     
        .provider-row td {
          padding: 6px 8px;
        }
     
        .sub-row td {
          font-size: 12px;
          border-top: none;
          font-style: normal !important;
          font-weight: normal !important;
        }
     
        .statement-table .subtotal-row td {
          text-align: right;
          font-weight: bold;
        }
  
        .aging-summary {
          margin-top: 20px;
        }
  
        .aging-table {
          width: 100%;
          border-collapse: collapse;
        }
  
        .aging-table th {
          background-color: #004aad;
          color: white;
          border: 1px solid #000;
          padding: 4px;
          text-align: center;
          font-size: 13px;
        }
  
        .aging-table td {
          text-align: right;
          border: 1px solid #000;
          padding: 4px;
        }
  
        .statement-footer {
          display: flex;
          justify-content: space-between;
          margin-top: 40px;
          padding-top: 15px;
          font-size: 12px;
          color: #666;
        }
  
        @media print {
          .header-controls {
            display: none !important;
          }
    
          .statement-container {
            padding: 0 !important;
          }
    
          .statement-section {
            margin-bottom: 0 !important;
            page-break-inside: avoid;
          }
    
          .print-button {
            display: none !important;
          }
       
          .client-payment-wrapper {
            display: flex !important;
            page-break-inside: avoid;
          }
       
          /* Fix for table headers in print */
          .aging-table th,
          .statement-table th {
            color: #000 !important;
            background-color: #fff !important;
            border: 1px solid #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
       
          .aging-table td,
          .statement-table td {
            border: 1px solid #000 !important;
          }
       
          .provider-row {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
       
          /* Ensure proper page breaks */
          .statement-table {
            page-break-inside: auto !important;
          }
       
          tbody {
            page-break-inside: auto !important;
          }
       
          tr, td, th {
            page-break-inside: avoid !important;
          }
       
          thead {
            display: table-header-group;
          }
        }
      `}</style>
    </div>
  );
};
export default ReReprint;